import React, { useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Star, Flame, Users, Award, ArrowRight, Calendar, X, Play } from "lucide-react";
import { TornEdge } from "../../components/sport/TornEdge";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

// Hardcoded milestones — would be CMS-driven in production via /api/sport/timeline
const TIMELINE = [
  { year: "1968", type: "creation", icon: Star, title: "Naissance du club", description: "Fondation du Club Sportif Istréen Handball.", color: PRIMARY, hasGallery: false },
  { year: "1995", type: "step", icon: Users, title: "Montée en Nationale", description: "Le club accède à la Nationale 1 et structure sa formation.", color: "#3a1f5c", hasGallery: false },
  { year: "2008", type: "title", icon: Trophy, title: "Champion de Pro D2", description: "Première montée historique en Division 1 ! Saison de tous les superlatifs.", color: SECONDARY, hasGallery: true, galleryTag: "champion-2008" },
  { year: "2014", type: "step", icon: Flame, title: "Maintien en D1", description: "Une saison de combat, le club s'installe parmi l'élite française.", color: "#ff3a8c", hasGallery: false },
  { year: "2018", type: "title", icon: Award, title: "1/2 finale Coupe de France", description: "Une épopée mémorable. Démonstration face aux cadors européens.", color: SECONDARY, hasGallery: true, galleryTag: "coupe-2018" },
  { year: "2022", type: "title", icon: Trophy, title: "Champion Proligue", description: "Retour triomphal en Liqui Moly StarLigue. Festival de buts à la Halle.", color: SECONDARY, hasGallery: true, galleryTag: "champion-proligue-2022" },
  { year: "2024", type: "step", icon: Star, title: "Maintien historique en StarLigue", description: "Une saison épique, plusieurs exploits face aux ténors du championnat.", color: PRIMARY, hasGallery: true, galleryTag: "maintien-2024" },
  { year: "2025", type: "step", icon: Flame, title: "Saison 2025-2026", description: "Nouveau roster, nouvelles ambitions. Plus que jamais Terre de Handball.", color: SECONDARY, hasGallery: false },
];

// Mocked photo galleries (replace with CMS later)
const GALLERIES = {
  "champion-2008": {
    title: "Sacre Pro D2 — 2008",
    items: [
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F5F6A0557-scaled-e1776434253613.jpg", caption: "Célébration finale" },
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg", caption: "Le trophée" },
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F5F6A6737-scaled-e1771509757955.jpg", caption: "Vestiaires" },
    ],
  },
  "coupe-2018": {
    title: "Demi-finale Coupe de France 2018",
    items: [
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A7467-scaled-e1773324091159.jpg", caption: "Match aller" },
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F5F6A6737-scaled-e1771509757955.jpg", caption: "Public en feu" },
    ],
  },
  "champion-proligue-2022": {
    title: "Champions Proligue 2022",
    items: [
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg", caption: "Tour d'honneur" },
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg", caption: "Coupe levée" },
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg", caption: "L'équipe" },
    ],
  },
  "maintien-2024": {
    title: "Maintien Historique 2024",
    items: [
      { type: "image", src: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A9819-scaled-e1774454113383.jpg", caption: "Action décisive" },
    ],
  },
};

function GalleryModal({ tag, onClose }) {
  const g = GALLERIES[tag];
  if (!g) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm overflow-y-auto"
      data-testid="gallery-modal"
    >
      <div className="min-h-full flex items-start justify-center p-4 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-5xl w-full">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-pink-400 text-[11px] font-black uppercase tracking-widest mb-1">Galerie</div>
              <h3 className="text-white font-black text-2xl sm:text-3xl uppercase italic">{g.title}</h3>
            </div>
            <button data-testid="gallery-close" onClick={onClose} className="w-11 h-11 rounded-full bg-white/10 hover:bg-pink-500 hover:text-black text-white transition flex items-center justify-center">
              <X size={20}/>
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {g.items.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl overflow-hidden bg-white/5 border border-white/10"
              >
                {it.type === "image" ? (
                  <img src={it.src} alt={it.caption} className="w-full aspect-[4/3] object-cover"/>
                ) : (
                  <div className="aspect-video">
                    <iframe src={it.src} title={it.caption} allow="autoplay; encrypted-media" className="w-full h-full"/>
                  </div>
                )}
                <div className="px-4 py-3 text-white/80 text-sm">{it.caption}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function SportHistoire() {
  const [openGallery, setOpenGallery] = useState(null);

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-16 overflow-hidden" style={{ background: `linear-gradient(135deg, ${BG_DARK} 0%, ${PRIMARY} 100%)` }}>
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 30%, rgba(245,166,35,0.4), transparent 60%)" }}/>
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-pink-400 font-black tracking-[0.4em] text-xs uppercase mb-3">
            55 ans de passion
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 0.95 }}>
            Notre <span style={{ color: SECONDARY }}>histoire</span>
          </motion.h1>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">Du club amateur de 1968 à l'élite de la Liqui Moly StarLigue : retour sur les moments qui ont écrit notre légende.</p>
        </div>
        <TornEdge position="bottom" color="#ffffff" variant="shred" height={56}/>
      </section>

      {/* TIMELINE */}
      <section className="relative py-20" style={{ background: "#fbf7fa" }}>
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-pink-600 font-black tracking-[0.3em] text-xs uppercase mb-2">Timeline</div>
            <h2 className="font-black text-3xl sm:text-4xl uppercase italic" style={{ color: PRIMARY }}>
              Les moments <span style={{ color: SECONDARY }}>qui comptent</span>
            </h2>
            <p className="text-slate-500 text-sm mt-3 italic">Cliquez sur les titres remportés <Trophy size={12} className="inline text-pink-500"/> pour accéder à la galerie</p>
          </div>

          {/* Vertical timeline */}
          <div className="relative">
            {/* Animated central line */}
            <motion.div
              initial={{ height: 0 }}
              whileInView={{ height: "100%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: "easeOut" }}
              className="absolute left-1/2 -translate-x-1/2 top-0 w-1 rounded-full"
              style={{ background: `linear-gradient(180deg, ${PRIMARY}, ${SECONDARY}, ${PRIMARY})` }}
            />

            <ul className="space-y-12">
              {TIMELINE.map((ev, i) => {
                const left = i % 2 === 0;
                const isTitle = ev.type === "title";
                return (
                  <motion.li
                    key={ev.year + ev.title}
                    initial={{ opacity: 0, x: left ? -40 : 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: [0.21, 0.94, 0.4, 1] }}
                    className={`relative grid grid-cols-[1fr,80px,1fr] items-center gap-4`}
                    data-testid={`timeline-event-${ev.year}`}
                  >
                    {/* Left content */}
                    <div className={`text-right ${left ? "" : "invisible md:visible md:opacity-0"}`}>
                      {left && (
                        <button
                          data-testid={`timeline-card-${ev.year}`}
                          disabled={!ev.hasGallery}
                          onClick={() => ev.hasGallery && setOpenGallery(ev.galleryTag)}
                          className={`group inline-block text-left rounded-2xl p-5 sm:p-6 max-w-md transition-all ${isTitle ? "shadow-2xl hover:shadow-[0_30px_60px_rgba(245,166,35,0.45)]" : "shadow-xl hover:shadow-2xl"} ${ev.hasGallery ? "cursor-pointer hover:-translate-y-1" : "cursor-default"}`}
                          style={{
                            background: isTitle ? `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` : "white",
                            border: isTitle ? "none" : `1px solid #e5e0e9`,
                          }}
                        >
                          <div className={`flex items-center gap-2 ${isTitle ? "text-[#1a0512]" : "text-pink-600"}`}>
                            <ev.icon size={16}/>
                            <span className="text-[11px] font-black uppercase tracking-widest">{ev.year} · {isTitle ? "Titre remporté" : ev.type === "creation" ? "Création" : "Étape clé"}</span>
                          </div>
                          <h3 className={`mt-2 font-black text-xl uppercase italic leading-tight ${isTitle ? "text-[#1a0512]" : ""}`} style={{ color: isTitle ? "#1a0512" : PRIMARY }}>
                            {ev.title}
                          </h3>
                          <p className={`mt-2 text-sm ${isTitle ? "text-[#1a0512]/85 font-medium" : "text-slate-600"}`}>
                            {ev.description}
                          </p>
                          {ev.hasGallery && (
                            <div className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest ${isTitle ? "text-[#1a0512]" : "text-pink-600"} group-hover:gap-2.5 transition`}>
                              Voir la galerie <ArrowRight size={12}/>
                            </div>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Center node */}
                    <div className="flex justify-center">
                      <motion.div
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                        className="relative w-14 h-14 rounded-full flex items-center justify-center text-white shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
                        style={{ background: ev.color }}
                      >
                        <ev.icon size={20} fill={isTitle ? "currentColor" : "transparent"}/>
                        {isTitle && (
                          <motion.span
                            className="absolute inset-0 rounded-full"
                            style={{ border: `2px solid ${SECONDARY}` }}
                            animate={{ scale: [1, 1.6, 1.6], opacity: [0.6, 0, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                          />
                        )}
                      </motion.div>
                    </div>

                    {/* Right content */}
                    <div className={`text-left ${!left ? "" : "invisible md:visible md:opacity-0"}`}>
                      {!left && (
                        <button
                          data-testid={`timeline-card-${ev.year}`}
                          disabled={!ev.hasGallery}
                          onClick={() => ev.hasGallery && setOpenGallery(ev.galleryTag)}
                          className={`group inline-block text-left rounded-2xl p-5 sm:p-6 max-w-md transition-all ${isTitle ? "shadow-2xl hover:shadow-[0_30px_60px_rgba(245,166,35,0.45)]" : "shadow-xl hover:shadow-2xl"} ${ev.hasGallery ? "cursor-pointer hover:-translate-y-1" : "cursor-default"}`}
                          style={{
                            background: isTitle ? `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` : "white",
                            border: isTitle ? "none" : `1px solid #e5e0e9`,
                          }}
                        >
                          <div className={`flex items-center gap-2 ${isTitle ? "text-[#1a0512]" : "text-pink-600"}`}>
                            <ev.icon size={16}/>
                            <span className="text-[11px] font-black uppercase tracking-widest">{ev.year} · {isTitle ? "Titre remporté" : ev.type === "creation" ? "Création" : "Étape clé"}</span>
                          </div>
                          <h3 className={`mt-2 font-black text-xl uppercase italic leading-tight ${isTitle ? "text-[#1a0512]" : ""}`} style={{ color: isTitle ? "#1a0512" : PRIMARY }}>
                            {ev.title}
                          </h3>
                          <p className={`mt-2 text-sm ${isTitle ? "text-[#1a0512]/85 font-medium" : "text-slate-600"}`}>
                            {ev.description}
                          </p>
                          {ev.hasGallery && (
                            <div className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest ${isTitle ? "text-[#1a0512]" : "text-pink-600"} group-hover:gap-2.5 transition`}>
                              Voir la galerie <ArrowRight size={12}/>
                            </div>
                          )}
                        </button>
                      )}
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {openGallery && <GalleryModal tag={openGallery} onClose={() => setOpenGallery(null)}/>}
    </div>
  );
}
